import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getUserPointSummary } from '../apiClient';
import { useJWTAuth } from '../JWTAuthContext';

interface PointMotivationData {
  total_points: number;
  available_points: number;
  current_week_points: number;
  weekly_limit: number;
}

const PointsMotivationBanner: React.FC = () => {
  const { user } = useJWTAuth();
  const [pointData, setPointData] = useState<PointMotivationData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchPointSummary = async () => {
      if (user && user.id) {
        setIsLoading(true);
        
        try {
          const response = await getUserPointSummary(user.id);
          if (response && response.status === 'success' && response.result) {
            setPointData({
              total_points: response.result.points.total_points,
              available_points: response.result.points.available_points,
              current_week_points: response.result.points.current_week_points,
              weekly_limit: response.result.points.weekly_limit
            });
          }
        } catch (error) {
          console.error('Failed to fetch point summary:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchPointSummary();
  }, [user]);

  const getMotivationalMessage = () => {
    if (!pointData) return "Start your quiz journey today!";
    
    const totalPoints = pointData.total_points;
    const weeklyProgress = (pointData.current_week_points / pointData.weekly_limit) * 100;

    if (totalPoints === 0) {
      return "🎯 Take your first quiz and start earning points!";
    } else if (totalPoints < 500) {
      return "🚀 Great start! Keep building your point collection!";
    } else if (totalPoints < 1500) {
      return "⭐ Awesome progress! You're on fire!";
    } else if (totalPoints < 3000) {
      return "🏆 Fantastic! You're becoming a quiz master!";
    } else if (weeklyProgress < 50) {
      return "💪 You still have room to earn more points this week!";
    } else if (weeklyProgress < 80) {
      return "🔥 You're crushing it this week! Keep going!";
    } else {
      return "👑 Amazing dedication! You're almost at your weekly limit!";
    }
  };

  const getPointsDisplay = () => {
    if (isLoading) return "---";
    if (!pointData) return "0";
    return pointData.total_points.toLocaleString();
  };

  const getAvailablePointsDisplay = () => {
    if (isLoading) return "---";
    if (!pointData) return "0";
    return pointData.available_points.toLocaleString();
  };

  return (
    <div className="card border-0 shadow-lg mb-4 overflow-hidden" 
         style={{ 
           background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
           borderRadius: '20px'
         }}>
      <div className="card-body p-4 text-white position-relative">
        {/* Background decoration */}
        <div className="position-absolute top-0 end-0 opacity-10" 
             style={{ 
               fontSize: '8rem', 
               lineHeight: '1', 
               marginTop: '-1rem', 
               marginRight: '-1rem',
               animation: 'float 3s ease-in-out infinite',
               zIndex: 1,
               pointerEvents: 'none'
             }}>
          💎
        </div>
        
        {/* Add keyframe animations */}
        <style>
          {`
            @keyframes float {
              0%, 100% { transform: translateY(0px) rotate(0deg); }
              50% { transform: translateY(-10px) rotate(5deg); }
            }
            
            @keyframes coinSpin {
              0% { transform: rotateY(0deg); }
              50% { transform: rotateY(180deg); }
              100% { transform: rotateY(360deg); }
            }
            
            @keyframes pulse {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.05); }
            }
            
            @keyframes buttonBounce {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.02); }
            }
            
            @keyframes glow {
              0%, 100% { box-shadow: 0 4px 15px rgba(0,0,0,0.1), 0 0 20px rgba(255,255,255,0.1); }
              50% { box-shadow: 0 6px 20px rgba(0,0,0,0.15), 0 0 30px rgba(255,255,255,0.2); }
            }
          `}
        </style>
        
        <div className="row align-items-center">
          <div className="col-12 col-md-8 mb-3 mb-md-0">
            <div className="d-flex align-items-center mb-3">
              <div className="bg-white bg-opacity-20 rounded-circle p-3 me-3"
                   style={{ animation: 'pulse 2s ease-in-out infinite' }}>
                <i className="fas fa-coins text-warning" 
                   style={{ 
                     fontSize: '2rem',
                     animation: 'coinSpin 4s linear infinite'
                   }}></i>
              </div>
              <div>
                <h2 className="mb-1 fw-bold text-white" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.6)' }}>
                  {getPointsDisplay()} <span className="fs-5 text-white" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>Points</span>
                </h2>
                <p className="mb-0 text-white" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
                  <i className="fas fa-wallet me-2"></i>
                  {getAvailablePointsDisplay()} available to spend
                </p>
              </div>
            </div>

            <div className="mb-3">
              <h5 className="mb-2 fw-normal text-white" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
                {getMotivationalMessage()}
              </h5>
              <p className="mb-0 text-white small" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
                💡 Earn up to 540 points per quiz (360 base + 120 hard level + 60 no-skip bonus)
              </p>
            </div>

            {/* Weekly Progress Indicator */}
            {pointData  && pointData.current_week_points > 1200 && (
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <small className="text-white-75">This Week's Progress</small>
                  <small className="text-white-75">
                    {pointData.current_week_points} / {pointData.weekly_limit}
                  </small>
                </div>
                <div className="progress bg-white bg-opacity-20" style={{ height: '6px' }}>
                  <div 
                    className="progress-bar bg-warning"
                    style={{ 
                      width: `${Math.min((pointData.current_week_points / pointData.weekly_limit) * 100, 100)}%` 
                    }}
                  ></div>
                </div>
              </div>
            )}
          </div>

          <div className="col-12 col-md-4 text-center text-md-end">
            <div className="d-grid gap-2">
              <Link 
                to="/custom-quiz-generator" 
                className="btn btn-light btn-lg fw-bold"
                style={{ 
                  borderRadius: '15px',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                  border: 'none',
                  animation: 'buttonBounce 2s ease-in-out infinite, glow 3s ease-in-out infinite',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.2), 0 0 40px rgba(255,255,255,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                }}
              >
                <i className="fas fa-plus-circle me-2 text-primary"></i>
                Start New Quiz
              </Link>
              
              <div className="text-center mt-2">
                <small className="text-white-75 d-block">
                  <i className="fas fa-gift me-1"></i>
                  Earn points • Unlock AI Tutor
                </small>
                <div className="d-flex justify-content-center mt-2 gap-2">
                  <span className="badge bg-warning bg-opacity-75 text-dark">
                    <i className="fas fa-star me-1"></i>
                    360 pts max
                  </span>
                  <span className="badge bg-info bg-opacity-75">
                    <i className="fas fa-robot me-1"></i>
                    120 pts/min
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Floating achievement indicators */}
        {pointData && pointData.total_points > 0 && (
          <div className="position-absolute bottom-0 start-0 p-3">
            <div className="d-flex gap-2">
              {pointData.total_points >= 500 && (
                <span className="badge bg-success bg-opacity-75 animate__animated animate__pulse animate__infinite">
                  🏅 500+ Club
                </span>
              )}
              {pointData.total_points >= 1500 && (
                <span className="badge bg-warning bg-opacity-75 animate__animated animate__pulse animate__infinite">
                  ⭐ 1500+ Star
                </span>
              )}
              {pointData.total_points >= 3000 && (
                <span className="badge bg-danger bg-opacity-75 animate__animated animate__pulse animate__infinite">
                  👑 3000+ Master
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PointsMotivationBanner;